import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Row,
  Tooltip
} from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import useApi from 'hooks/useApi';
import {
  getExportAllData,
  getFarmTableData
} from 'api/endpoints/TerrestrialServices';
import { get, isEmpty } from 'lodash';
import { jwtDecode } from 'jwt-decode';
import { getDistrictData, getStateData } from 'api/endpoints/Location';
import {
  getFarmerCard,
  getSmsCard,
  getFarmerChart,
  getGenderChart,
  getSmsGroupChart,
  getMarketPriceDashboardCharts
} from 'api/endpoints/Dashboard';
import ObservationRender from 'screens/common/ObservationRender';
import MarketPriceObservationRender from 'components/dashboards/monitoring/ObservationRender';
import FarmLevelReportTable from './ReportTable';
import CardDropdown from 'components/common/CardDropdown';
import RangeDatePicker from 'screens/common/RageDatePicker';
import StatisticsCard from 'components/dashboards/saas/stats-cards/StatisticsCard';
import { lmsStatistics } from 'data/dashboard/lms';
import DatePicker from 'components/dashboards/manage-projects/DatePicker';
import { Link } from 'react-router-dom';
import ReportTable from './ReportTable';
import WeatherCards from './weatherCards';
import { getTenantFromToken } from 'utils/routeUtils';
import CustomSpinner from 'components/common/CustomSpinner';
import StatiticsCardsSkeleton from 'components/dashboards/default/skeleton/StatiticsCardsSkeleton';
import { fieldOfficerReportApi } from 'api/FieldOfficerApi';

function WeatherDashboard() {
  const tenantid = getTenantFromToken();

  const getFarmerCardApi = useApi(getFarmerCard);
  const getSmsCardApi = useApi(getSmsCard);
  const getFarmerChartApi = useApi(getFarmerChart);
  const getGenderChartApi = useApi(getGenderChart);
  const getSmsGroupChartApi = useApi(getSmsGroupChart);
  const getMarketPriceChartsApi = useApi(getMarketPriceDashboardCharts);
  const getDistrictDataApi = useApi(getDistrictData);
  const getFarmTableDataAPI = useApi(getFarmTableData);

  const [farmerCardData, setFarmerCardData] = useState({});
  const [smsCardData, setSmsCardData] = useState({});
  const [chartData, setChartData] = useState({});
  const [farmerChartData, setFarmerChartData] = useState([]);
  const [genderChartData, setGenderChartData] = useState([]);
  const [smsGroupChartData, setSmsGroupChartData] = useState([]);
  const [farmerCountChart, setFarmerCountChart] = useState({});
  const [marketPriceChartData, setMarketPriceChartData] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [farmData, setFarmData] = useState([]);
  const [showSpinner, setShowSpinner] = useState(true);

  useEffect(() => {
    const formattedStartDate = startDate ? formatDate(startDate) : '';
    const formattedEndDate = endDate ? formatDate(endDate) : '';
    getFarmerCardApi.request({
      start_date: formattedStartDate,
      end_date: formattedEndDate
    });
    getSmsCardApi.request({
      start_date: formattedStartDate,
      end_date: formattedEndDate
    });
  }, [startDate, endDate]);

  useEffect(() => {
    const data = get(getFarmerCardApi, 'data.results', []);
    setFarmerCardData(data);
  }, [getFarmerCardApi.data]);

  useEffect(() => {
    const data = get(getSmsCardApi, 'data.results', []);
    setSmsCardData(data);
  }, [getSmsCardApi.data]);

  useEffect(() => {
    const formattedStartDate = startDate ? formatDate(startDate) : '';
    const formattedEndDate = endDate ? formatDate(endDate) : '';
    getFarmerChartApi.request({
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      limit: 10
    });
    getGenderChartApi.request({
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      limit: 10
    });
    getSmsGroupChartApi.request({
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      limit: 10
    });
    getMarketPriceChartsApi.request({
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      limit: 10
    });
  }, [startDate, endDate]);

  useEffect(() => {
    const data = get(getFarmerChartApi, 'data.results', []);
    setFarmerChartData(data);
  }, [getFarmerChartApi.data]);

  useEffect(() => {
    const data = get(getGenderChartApi, 'data.results', []);
    setGenderChartData(data);
  }, [getGenderChartApi.data]);

  useEffect(() => {
    const data = get(getSmsGroupChartApi, 'data.results', []);
    setSmsGroupChartData(data);
  }, [getSmsGroupChartApi.data]);

  useEffect(() => {
    const fetchFarmerCountData = async () => {
      try {
        const farmerCountData = await fieldOfficerReportApi.fieldOfferChart();
        setFarmerCountChart({ fieldOfficer: farmerCountData.data.results });
      } catch (error) {
        console.error('Error fetching farmer count data:', error);
      }
    };

    fetchFarmerCountData();
  }, [tenantid]);

  useEffect(() => {
    const newData = get(getMarketPriceChartsApi, 'data.results', []);
    setMarketPriceChartData(newData);
  }, [getMarketPriceChartsApi.data]);

  useEffect(() => {
    let limit = 5;
    let cursor = 0;
    let filter = '';
    getFarmTableDataAPI.request({ cursor, limit, filter });
  }, []);

  useEffect(() => {
    const tabledata = get(getFarmTableDataAPI, 'data.results', []);
    setFarmData(tabledata.data);
  }, [getFarmTableDataAPI.data]);

  const formatDate = date => {
    const d = new Date(date);
    const day = ('0' + d.getDate()).slice(-2);
    const month = ('0' + (d.getMonth() + 1)).slice(-2); // Month is zero-based
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (start, end) => {
    if ((start && end) || (start == '' && end == '')) {
      setStartDate(start);
      setEndDate(end);
    }
  };

  const cardData = { ...farmerCardData, ...smsCardData };
  const statsData = Object.keys(cardData).map((key, index) => {
    const item = cardData[key];

    const stat = {
      sign: item.percentage > 0 ? '+' : '',
      value: item.count,
      info: ``,
      title: item.title,
      suffix: '',
      desc: ''
    };

    return (
      <Col key={key} sm={6} md={6}>
        <StatisticsCard
          stat={stat}
          style={{ minWidth: '12rem', height: '100%' }}
          index={index}
        />
      </Col>
    );
  });

  const isLoading =
    getFarmerCardApi.loading ||
    getSmsCardApi.loading ||
    getFarmerChartApi.loading ||
    getGenderChartApi.loading ||
    getSmsGroupChartApi.loading ||
    getMarketPriceChartsApi.loading;

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowSpinner(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  return (
    <div>
      {showSpinner ? (
        <StatiticsCardsSkeleton />
      ) : (
        <>
          <Row className="g-3 mb-6 mt-1">
            <Col md="4" className="mt-0">
              <RangeDatePicker onDateChange={handleDateChange} />
            </Col>
          </Row>
          {cardData &&
            Object.values(cardData).every(item => item?.count > 0) && (
              <Row className="g-3 mb-4">{statsData}</Row>
            )}

          {!isEmpty(farmerChartData) &&
            !isEmpty(genderChartData) &&
            !isEmpty(smsGroupChartData) && (
              <Row className="g-3 mb-3">
                <ObservationRender
                  key="farmerChart"
                  dataset={farmerChartData}
                  loading={getFarmerChartApi.loading}
                />
                <ObservationRender
                  key="genderChart"
                  dataset={genderChartData}
                  loading={getGenderChartApi.loading}
                />
                <ObservationRender
                  key="smsGroupChart"
                  dataset={smsGroupChartData}
                  loading={getSmsGroupChartApi.loading}
                />
              </Row>
            )}
          {!isEmpty(farmerCountChart) && (
            <Row className="g-3 mb-3">
              <ObservationRender
                dataset={farmerCountChart}
                loading={fieldOfficerReportApi?.loading}
              />
            </Row>
          )}
          <WeatherCards />
          {!isEmpty(farmData) && (
            <Row className="g-3 mb-3">
              <Col className="my-1">
                <Card>
                  <FalconCardHeader
                    title={'Farm-level Report'}
                    titleTag="h5"
                    light={true}
                    endEl={
                      <>
                        <Link
                          to={`/${tenantid}/farmlevelReport`}
                          style={{ fontSize: '14px' }}
                        >
                          See More
                        </Link>
                      </>
                    }
                  />
                  <CardBody>
                    <ReportTable />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
          {!isEmpty(marketPriceChartData) && (
            <Row className="g-3 mb-3">
              <MarketPriceObservationRender
                key={'marketPriceChart'}
                observation={marketPriceChartData}
                loading={getMarketPriceChartsApi?.loading}
              />
            </Row>
          )}
        </>
      )}
    </div>
  );
}

export default WeatherDashboard;
